import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../../api/axiosInstance';

const emptyForm = {
  _id: '',
  name: '',
  description: '',
  taskType: 'report_adv',
  cronExpression: '0 7 * * *',
  timezone: 'Indian/Reunion',
  enabled: true,
  payload: {
    periodMode: 'daily_window',
    title: 'Rapport ADV planifié',
    subject: 'Rapport ADV planifié',
    to: 'adv@toolbox.re',
    startDayOfMonth: 1,
    endDayOfMonth: 1,
    boundaryHour: 0,
    boundaryMinute: 0,
  },
};

const taskTypeLabels = {
  report_adv: 'Rapport ADV',
  level_devices_sync: 'Sync Level.io devices',
  signitic_sync: 'Sync Signitic',
};

const periodModeLabels = {
  daily_window: 'Fenêtre quotidienne',
  calendar_day: 'Jour calendrier',
  previous_calendar_month: 'Mois précédent',
  monthly_day_range: 'Plage mensuelle',
  custom_periods: 'Périodes personnalisées',
  relative_periods: 'Périodes relatives',
};

function formatDate(value) {
  if (!value) return 'Jamais';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function CronSettingsPage() {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState('');
  const [feedback, setFeedback] = useState(null);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/settings/cron/tasks');
      setTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Impossible de charger les tâches cron.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const resetForm = () => setForm(emptyForm);

  const editTask = (task) => {
    setForm({
      ...emptyForm,
      ...task,
      payload: { ...emptyForm.payload, ...(task.payload || {}) },
    });
  };

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updatePayload = (field, value) => setForm((current) => ({
    ...current,
    payload: { ...current.payload, [field]: value },
  }));

  const saveTask = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        taskType: form.taskType,
        cronExpression: form.cronExpression,
        timezone: form.timezone,
        enabled: form.enabled,
        payload: form.taskType === 'report_adv' ? form.payload : { notifyOnSuccess: true },
      };
      if (form._id) {
        await axios.put(`/api/settings/cron/tasks/${form._id}`, payload);
      } else {
        await axios.post('/api/settings/cron/tasks', payload);
      }
      setFeedback({ type: 'success', message: 'Tâche cron sauvegardée.' });
      resetForm();
      await loadTasks();
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Sauvegarde impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const deleteTask = async (task) => {
    setFeedback(null);
    try {
      await axios.delete(`/api/settings/cron/tasks/${task._id}`);
      setFeedback({ type: 'success', message: 'Tâche supprimée.' });
      await loadTasks();
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Suppression impossible.' });
    }
  };

  const runTask = async (task) => {
    setRunningId(task._id);
    setFeedback(null);
    try {
      const { data } = await axios.post(`/api/settings/cron/tasks/${task._id}/run`);
      setFeedback({ type: 'success', message: data.message || 'Tâche exécutée.' });
      await loadTasks();
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Exécution impossible.' });
    } finally {
      setRunningId('');
    }
  };

  const toggleTask = async (task) => {
    await axios.put(`/api/settings/cron/tasks/${task._id}`, {
      ...task,
      enabled: !task.enabled,
    });
    await loadTasks();
  };

  const columns = [
    { accessorKey: 'name', header: 'Nom', size: 220 },
    {
      accessorKey: 'taskType',
      header: 'Type',
      Cell: ({ cell }) => taskTypeLabels[cell.getValue()] || cell.getValue(),
    },
    { accessorKey: 'cronExpression', header: 'Cron' },
    { accessorKey: 'timezone', header: 'Timezone' },
    {
      accessorKey: 'enabled',
      header: 'Actif',
      Cell: ({ row }) => (
        <Switch checked={Boolean(row.original.enabled)} onChange={() => toggleTask(row.original)} />
      ),
    },
    {
      accessorKey: 'lastRunStatus',
      header: 'Dernier statut',
      Cell: ({ row }) => row.original.lastRunStatus ? (
        <Chip size="small" color={row.original.lastRunStatus === 'success' ? 'success' : 'error'} label={row.original.lastRunStatus} />
      ) : null,
    },
    {
      accessorKey: 'lastRunAt',
      header: 'Dernière exécution',
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    { accessorKey: 'lastRunMessage', header: 'Message', size: 260 },
    {
      id: 'runHistory',
      header: 'Historique',
      size: 300,
      Cell: ({ row }) => {
        const runs = [...(row.original.runHistory || [])].reverse().slice(0, 3);
        if (!runs.length) return null;
        return (
          <Stack spacing={0.5}>
            {runs.map((run, index) => (
              <Typography key={`${run.finishedAt || index}-${run.status}`} variant="caption" color={run.status === 'success' ? 'success.main' : 'error.main'}>
                {formatDate(run.finishedAt)} · {run.message}
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => editTask(row.original)}>Modifier</Button>
          <Button size="small" startIcon={<PlayArrowRoundedIcon />} onClick={() => runTask(row.original)} disabled={runningId === row.original._id}>
            Lancer
          </Button>
          <Button size="small" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => deleteTask(row.original)}>
            Supprimer
          </Button>
        </Stack>
      ),
    },
  ];

  const table = useMaterialReactTable({
    columns,
    data: tasks,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    initialState: { showColumnFilters: true, showGlobalFilter: true, density: 'compact' },
    state: { isLoading: loading },
    getRowId: (row) => row._id,
  });

  return (
    <Stack spacing={2.5}>
      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField label="Nom" size="small" value={form.name} onChange={(event) => updateField('name', event.target.value)} sx={{ minWidth: { md: 240 } }} />
            <TextField label="Type" size="small" select value={form.taskType} onChange={(event) => updateField('taskType', event.target.value)} sx={{ minWidth: { md: 220 } }}>
              {Object.entries(taskTypeLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField label="Expression cron" size="small" value={form.cronExpression} onChange={(event) => updateField('cronExpression', event.target.value)} sx={{ minWidth: { md: 180 } }} />
            <TextField label="Timezone" size="small" value={form.timezone} onChange={(event) => updateField('timezone', event.target.value)} sx={{ minWidth: { md: 180 } }} />
          </Stack>

          <TextField label="Description" size="small" value={form.description} onChange={(event) => updateField('description', event.target.value)} fullWidth />

          {form.taskType === 'report_adv' && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="Mode période" size="small" select value={form.payload.periodMode} onChange={(event) => updatePayload('periodMode', event.target.value)} sx={{ minWidth: { md: 220 } }}>
                {Object.entries(periodModeLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              <TextField label="Destinataire" size="small" value={form.payload.to} onChange={(event) => updatePayload('to', event.target.value)} sx={{ minWidth: { md: 240 } }} />
              <TextField label="Titre" size="small" value={form.payload.title} onChange={(event) => updatePayload('title', event.target.value)} sx={{ minWidth: { md: 240 } }} />
              <TextField label="Sujet" size="small" value={form.payload.subject} onChange={(event) => updatePayload('subject', event.target.value)} sx={{ minWidth: { md: 240 } }} />
            </Stack>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel control={<Switch checked={form.enabled} onChange={(event) => updateField('enabled', event.target.checked)} />} label="Actif" />
            <Button variant="contained" onClick={saveTask} disabled={saving || !form.name || !form.cronExpression}>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</Button>
            <Button variant="outlined" onClick={resetForm}>Nouveau</Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Tâches planifiées</Typography>
          <MaterialReactTable table={table} />
        </Stack>
      </Paper>
    </Stack>
  );
}
